import * as Popover from '@radix-ui/react-popover'
import clsx from 'clsx'
import type { ForwardedRef } from 'react'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import type { ISelectInternalRef } from '@/components/shared/ui/Select/SelectTypes'

interface IPropTypes {
  children: React.ReactNode
  trigger: React.ReactNode
  disabled?: boolean
  searchable?: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
  onOpenChange: (open: boolean) => void
  dataTestId?: string
}

const SelectPopover = forwardRef(
  (
    {
      children,
      trigger,
      disabled,
      side = 'bottom',
      onOpenChange,
      dataTestId,
    }: IPropTypes,
    ref: ForwardedRef<ISelectInternalRef>
  ) => {
    const [open, setOpen] = useState(false)

    useImperativeHandle(ref, () => ({
      toggle: () => setOpen(!open),
      close: () => setOpen(false),
      open: () => setOpen(true),
    }))

    useEffect(() => {
      onOpenChange(open)
    }, [open])

    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          disabled={disabled}
          data-testid={dataTestId}
          className='w-full appearance-none rounded-md outline-[0.5px] outline-primary'
        >
          {trigger}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            onEscapeKeyDown={() => setOpen(false)}
            className={clsx(
              'z-[6] mt-1 max-h-96 w-full min-w-[11rem] overflow-y-auto rounded-[10px] border border-gray6 bg-snow px-0.5 pb-0.5 shadow-lg transition-opacity will-change-[transform,opacity] animate-in duration-200 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:bg-gray3'
            )}
            side={side}
            align='start'
            collisionPadding={8}
            onWheel={(e) => {
              const isModalOpen = document.querySelector('[data-modal]')

              if (!isModalOpen) return
              e.stopPropagation()
              const isScrollingDown = e.deltaY > 0
              if (isScrollingDown) {
                e.currentTarget.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'ArrowDown' })
                )
              } else {
                e.currentTarget.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'ArrowUp' })
                )
              }
            }}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    )
  }
)
SelectPopover.displayName = 'SelectPopover'

export default SelectPopover
